import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import ProjectStore from '../../flux/stores/ProjectStore';
import Actions from '../../flux/Actions';
import SceneEditor from './SceneEditor';

function EntityDetail({ entity, entityType, onEdit, onDelete }) {
   const [characters, setCharacters] = useState([]);

   useEffect(() => {
      const handleChange = () => setCharacters(ProjectStore.getCharacters());
      handleChange();
      ProjectStore.addChangeListener(handleChange);
      return () => ProjectStore.removeChangeListener(handleChange);
   }, []);

   const sceneCharacters = useMemo(() => {
      if (entityType !== 'scene') return [];
      const charactersById = new Map(characters.map((c) => [c.id, c]));
      return (entity.characterIds || []).map((id) => ({
         id,
         name: charactersById.get(id)?.name || 'Unknown',
         isPov: entity.povCharacterId === id,
      }));
   }, [characters, entity, entityType]);

   const renderSceneDetail = () => (
      <>
         <div className="mb-6">
            <SceneEditor
               key={entity.id}
               sceneId={entity.id}
               content={entity.script || ''}
            />
         </div>

         {entity.chapter && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-1">CHAPTER</h3>
               <p className="text-2xl font-display text-pearl">{entity.chapter}</p>
            </div>
         )}

         <div className="mb-6">
            <h3 className="text-xs font-mono text-ash mb-1">TITLE</h3>
            <h1 className="text-4xl font-serif text-pearl">{entity.title || 'Untitled Scene'}</h1>
         </div>

         {entity.time && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-1">TIME</h3>
               <p className="text-lg font-mono text-silver">{entity.time}</p>
            </div>
         )}

         {entity.description && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">DESCRIPTION</h3>
               <p className="text-pearl font-serif leading-relaxed whitespace-pre-wrap">
                  {entity.description}
               </p>
            </div>
         )}

         {entity.notes && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">NOTES</h3>
               <p className="text-silver font-mono text-sm whitespace-pre-wrap">
                  {entity.notes}
               </p>
            </div>
         )}

         {sceneCharacters.length > 0 && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">CHARACTERS</h3>
               <div className="flex flex-wrap gap-2">
                  {sceneCharacters.map(({ id, name, isPov }) => (
                     <button
                        type="button"
                        onClick={() => {
                           Actions.setActiveTab('characters');
                           Actions.selectCharacter(id);
                        }}
                        key={id}
                        className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${
                           isPov
                              ? 'border-amethyst bg-amethyst/10 text-pearl hover:bg-amethyst/20'
                              : 'border-slate/60 bg-charcoal/40 text-silver hover:bg-charcoal/60'
                        }`}
                     >
                        {name} {isPov ? '(POV)' : ''}
                     </button>
                  ))}
               </div>
            </div>
         )}
      </>
   );

   const renderCharacterDetail = () => (
      <>
         {entity.imageUrl && (
            <div className="mb-6">
               <div className="overflow-hidden rounded-lg border border-charcoal">
                  <img src={entity.imageUrl} alt={entity.name || 'Character'} className="w-full h-64 object-cover" />
               </div>
            </div>
         )}

         <div className="mb-6">
            <h3 className="text-xs font-mono text-ash mb-1">NAME</h3>
            <h1 className="text-4xl font-serif text-pearl">{entity.name || 'Unknown'}</h1>
         </div>

         <div className="grid grid-cols-2 gap-4 mb-6">
            {entity.role && (
               <div>
                  <h3 className="text-xs font-mono text-ash mb-1">ROLE</h3>
                  <p className="text-lg font-display text-silver">{entity.role}</p>
               </div>
            )}
            {entity.age && (
               <div>
                  <h3 className="text-xs font-mono text-ash mb-1">AGE</h3>
                  <p className="text-lg font-mono text-silver">{entity.age}</p>
               </div>
            )}
         </div>

         {entity.appearance && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">APPEARANCE</h3>
               <p className="text-pearl font-serif leading-relaxed whitespace-pre-wrap">
                  {entity.appearance}
               </p>
            </div>
         )}

         {entity.personality && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">PERSONALITY</h3>
               <p className="text-pearl font-serif leading-relaxed whitespace-pre-wrap">
                  {entity.personality}
               </p>
            </div>
         )}

         {entity.backstory && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">BACKSTORY</h3>
               <p className="text-pearl font-serif leading-relaxed whitespace-pre-wrap">
                  {entity.backstory}
               </p>
            </div>
         )}
      </>
   );

   const renderLocationDetail = () => (
      <>
         {entity.imageUrl && (
            <div className="mb-6">
               <div className="overflow-hidden rounded-lg border border-charcoal">
                  <img src={entity.imageUrl} alt={entity.name || 'Location'} className="w-full h-64 object-cover" />
               </div>
            </div>
         )}

         <div className="mb-6">
            <h3 className="text-xs font-mono text-ash mb-1">NAME</h3>
            <h1 className="text-4xl font-serif text-pearl">{entity.name || 'Unknown Location'}</h1>
         </div>

         {entity.type && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-1">TYPE</h3>
               <p className="text-lg font-display text-silver">{entity.type}</p>
            </div>
         )}

         {entity.description && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">DESCRIPTION</h3>
               <p className="text-pearl font-serif leading-relaxed whitespace-pre-wrap">
                  {entity.description}
               </p>
            </div>
         )}

         {entity.features && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">NOTABLE FEATURES</h3>
               <p className="text-silver font-mono text-sm whitespace-pre-wrap">
                  {entity.features}
               </p>
            </div>
         )}
      </>
   );

   const renderPlotDetail = () => (
      <>
         <div className="mb-6">
            <h3 className="text-xs font-mono text-ash mb-1">PLOT POINT</h3>
            <h1 className="text-4xl font-serif text-pearl">{entity.title || 'Untitled Plot Point'}</h1>
         </div>

         {entity.type && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-1">TYPE</h3>
               <p className="text-lg font-display text-silver uppercase">
                  {entity.type.replace(/_/g, ' ')}
               </p>
            </div>
         )}

         {entity.description && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">DESCRIPTION</h3>
               <p className="text-pearl font-serif leading-relaxed whitespace-pre-wrap">
                  {entity.description}
               </p>
            </div>
         )}

         {entity.impact && (
            <div className="mb-6">
               <h3 className="text-xs font-mono text-ash mb-2">IMPACT ON STORY</h3>
               <p className="text-silver font-mono text-sm whitespace-pre-wrap">
                  {entity.impact}
               </p>
            </div>
         )}
      </>
   );

   const renderDetail = () => {
      switch (entityType) {
         case 'scene':
            return renderSceneDetail();
         case 'character':
            return renderCharacterDetail();
         case 'location':
            return renderLocationDetail();
         case 'plot':
            return renderPlotDetail();
         default:
            return null;
      }
   };

   return (
      <motion.div
         initial={{ opacity: 0 }}
         animate={{ opacity: 1 }}
         transition={{ duration: 0.3 }}
         className="h-full flex flex-col"
      >
         <div className="flex-1 overflow-y-auto p-8">
            {renderDetail()}
         </div>
         <div className="p-4 border-t border-charcoal flex justify-end space-x-2">
            <button
               onClick={onEdit}
               className="px-4 py-2 bg-charcoal hover:bg-slate text-silver font-mono text-sm rounded transition-colors flex items-center space-x-2"
            >
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
               </svg>
               <span>EDIT</span>
            </button>
            <button
               onClick={onDelete}
               className="px-4 py-2 bg-blood/20 hover:bg-blood/30 text-rose font-mono text-sm rounded transition-colors flex items-center space-x-2"
            >
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1 1v3M4 7h16" />
               </svg>
               <span>DELETE</span>
            </button>
         </div>
      </motion.div>
   );
}

export default EntityDetail;
